import {
  DeleteOutlined, EditOutlined, PlusOutlined,
} from '@ant-design/icons';
import {
  Button, Space, Switch,
} from 'antd';
import { ModifierContext } from 'containers/MenuManagement/context';
import React, { useContext, useMemo, useState } from 'react';
import { priceTransformer } from 'utils';
import { captureException } from 'utils/errors';
import { getImage } from 'utils/image';
import { generateColumns } from 'utils/table';
import DietaryTags from 'components/DietaryTags';
import DraggableTable from 'components/DraggableTable';
import { ModalMode } from '../../../constants';
import { MenuContext } from '../../context';
import ItemDetailModal from './ItemDetailModal';
import { changeItemAvailability, deleteItem, reorderItemPosition } from './requests';

function ItemsTable({ category, categories, setCategories, availabilityLocked = false }) {
  const {
    itemsLoading, setItemsLoading, fetchMenuDetail, showMessage,
  } = useContext(MenuContext);
  const [mode, setMode] = useState(ModalMode.CLOSED);
  const [editingItem, setEditingItem] = useState();
  const { merchant } = useContext(ModifierContext);

  const onRemoveItem = (e, id) => {
    e.preventDefault();
    setItemsLoading(true);
    deleteItem(id).then(() => {
      fetchMenuDetail();
    })
      .catch((e) => {
        captureException(e);
        showMessage({ type: 'error' });
        setItemsLoading(false);
      });
  };

  const onChangeItemAvailability = (id) => {
    setItemsLoading(true);
    changeItemAvailability(id).then(() => {
      fetchMenuDetail();
    })
      .catch((e) => {
        captureException(e);
        showMessage({ type: 'error' });
        setItemsLoading(false);
      });
  };

  const sortColumn = {
    key: 'sort',
    width: 40,
    align: 'right',
  };

  const itemNameColumn = {
    title: 'Item Name',
    key: 'name',
    className: 'menu-item-name-cell',
    render: (_, data) => (
      <span className="menu-item-name-inner">
        {getImage({ data }) && (
          <img
            className="image"
            src={getImage({ data, size: 'md' })}
            alt={data?.name || ''}
            width={25}
            height={25}
            style={{ border: '1px solid #DDE1E4', borderRadius: 5, flexShrink: 0 }}
          />
        )}
        <span className="menu-item-name-text">{data.name}</span>
      </span>
    ),
  };

  const cateringColumn = merchant.is_catering && {
    title: 'Unit',
    key: 'catering_label',
  };

  const sizesColumn = {
    title: 'Sizes',
    key: 'sizes',
    align: 'center',
    className: 'menu-item-sizes-cell',
    render: (sizes) => (
      <span className="menu-item-sizes-text">
        {sizes?.filter((size) => size.price)
          .map((size) => size.name || 'One size').join(', ')}
      </span>
    ),
  };

  const priceColumn = {
    title: 'Base Price',
    key: 'value',
    width: 150,
    align: 'center',
    render: (value) => priceTransformer(value),
  };

  const tagsColumn = {
    title: 'Tags',
    key: 'dietary_tags',
    width: 190,
    align: 'center',
    render: (value) => <DietaryTags tags={value} maxCount={3} />,
  };

  const availabilityColumn = {
    title: 'Item availability',
    key: 'is_active',
    width: 160,
    align: 'center',
    className: 'menu-item-availability-cell',
    render: (value, record) => (
      <Switch
        checked={value}
        onChange={() => onChangeItemAvailability(record.id)}
        disabled={itemsLoading || availabilityLocked}
      />
    ),
  };

  const operationsColumn = {
    title: ' ',
    key: 'operation',
    width: 88,
    align: 'center',
    className: 'menu-item-actions-cell',
    render: (_, record) => (
      <Space size="small">
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingItem(record);
            setMode(ModalMode.EDIT);
          }}
        />
        <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => onRemoveItem(e, record.id)} />
      </Space>
    ),
  };

  const data = useMemo(() => category.items?.sort((a, b) => (a.item_position?.[0]?.position ?? 0) - (b.item_position?.[0]?.position ?? 0))
    ?.map((item) => ({ ...item, key: item.id })) || [], [category]);
  const reOrderItem = ({ items, categoryId }) => {
    const currentCategories = [...categories];
    const categoryIndex = currentCategories.findIndex((c) => c.id === categoryId);
    currentCategories[categoryIndex].items = items;
    setCategories([...currentCategories]);
  };

  const onReorderItemCallback = ({ id, position, categoryId }) => {
    setItemsLoading(true);
    reorderItemPosition({ itemId: id, categoryId, position }).then(() => {
    }).catch((e) => captureException(e))
      .finally(() => setItemsLoading(false));
  };

  const onDrawerClose = () => {
    setMode(ModalMode.CLOSED);
    setEditingItem();
  };
  const columnDefs = [
    sortColumn,
    itemNameColumn,
    cateringColumn,
    sizesColumn,
    priceColumn,
    tagsColumn,
    availabilityColumn,
    operationsColumn,
  ];
  const normalizedColumns = generateColumns(columnDefs.filter((c) => c));

  if (!data.length) {
    return (
      <>
        <Button type="text" style={{ margin: 8 }} icon={<PlusOutlined />} onClick={() => setMode(ModalMode.NEW)}>
          Add item
        </Button>
        <ItemDetailModal mode={mode} onClose={onDrawerClose} category={category} itemDetail={editingItem} />
      </>
    );
  }

  return (
    <>
      <DraggableTable
        className="menu-management-expanded-items-table"
        scroll={false}
        columns={normalizedColumns}
        dataSource={data}
        setDataSource={(items) => reOrderItem({ items, categoryId: category.id })}
        onDragCallback={({ id, position }) => onReorderItemCallback({ id, position, categoryId: category.id })}
      />
      <Button type="text" style={{ margin: 8 }} icon={<PlusOutlined />} onClick={() => setMode(ModalMode.NEW)}>
        Add item
      </Button>

      <ItemDetailModal mode={mode} onClose={onDrawerClose} category={category} itemDetail={editingItem} />
    </>
  );
}

export default ItemsTable;
